import {
    GRADE_OPTIONS,
    SPACETYPE_OPTIONS,
} from "~/lib/helpers/constants";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Label } from "~/components/ui/label";
import { SliderDemo } from "~/components/ui/custom-radix-components/slider";
import { ClientOnly } from "../../common/client-only";
import AutoPlaces from "../../Geosearch/AutoPlaces";
import { MAX_SQFT, MAX_SQM, SQM_TO_SQFT } from "~/lib/constants/generalConstants";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import AddListingTwoStepDrawer from "../../Drawers/AddListingTwoStepDrawer";
import AddListingTwoStepModal from "../AddListingTwoStepModal";
import { useState } from "react";

interface ContentProps {
    office: "premium" | "superior" | "economical";
    spaceType: string;
    desks: number;
    units: "sm" | "sf";
    area: number;
    setOffice: (val: "premium" | "superior" | "economical") => void;
    setSpaceType: (val: string) => void;
    setDesks: (val: number) => void;
    setUnits: (val: "sm" | "sf") => void;
    setArea: (val: number) => void;
    handleLocationSelect: (val: string, location?: string) => void;
    areaDisplaySqft: number;
    areaDisplaySqm: number;
    isMobile?: boolean;
    isSticky?: boolean;
    sliderMax?: number;
    handleAreaSliderChange?: (values: number[]) => void;
    sliderValue?: number;
}

export default function Content({
    office,
    spaceType,
    desks,
    units,
    area,
    setOffice,
    setSpaceType,
    setDesks,
    setUnits,
    setArea,
    handleLocationSelect,
    areaDisplaySqft,
    areaDisplaySqm,
    isMobile,
    isSticky = false,
    sliderMax,
    handleAreaSliderChange,
    sliderValue
}: ContentProps) {

    const handleLocalAreaSliderChange = (values: number[]) => {
        const sliderValue = values[0]
        if (units === "sm") {
            setArea(sliderValue)
        } else if (units === "sf") {
            setArea(Number((sliderValue / SQM_TO_SQFT).toFixed(2)))
        }
    }



    const localAreaDisplaySqm = Math.round(area)
    const localAreaDisplaySqft = Math.round(area * SQM_TO_SQFT)
    const localSliderValue = units === "sm" ? area : Math.round(area * SQM_TO_SQFT)
    const localSliderMax = units === "sm" ? MAX_SQM : MAX_SQFT

    const handleLocalLocationSelect = (newLocation: string, location?: string) => {
        // Forward AutoPlaces' second arg (the picked label) too — the area/city
        // URL has no coords, so the parent resolves the label to lat/lng.
        handleLocationSelect(newLocation, location)
    }

    const [addListingModal, setAddListingModal] = useState(false)
    return (
        <>
            <fieldset className="flex flex-col w-full bg-white space-y-4 md:gap-4">
                <legend className="sr-only">Estimate your office earnings</legend>
                {isMobile && addListingModal && (
                    <AddListingTwoStepDrawer onClose={() => setAddListingModal(false)} />
                )}
                {!isMobile && addListingModal && (
                    <AddListingTwoStepModal onClose={() => setAddListingModal(false)} />
                )}
                {/* Location */}
                <div className="mt-2 md:mt-4">
                    <div className="mb-2 font-semibold text-start text-sm">Location</div>
                    <ClientOnly>
                        {() => (
                            <AutoPlaces
                                onLocationSelect={handleLocalLocationSelect}
                                inputStyleClassName="rounded-md"
                                cityRestrict={true}
                                placeholder="Address or area"
                            />
                        )}
                    </ClientOnly>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Building Grade */}
                    <div className="block md:flex md:flex-col">
                        <div className="mb-2 font-semibold text-start text-sm">Building Grade</div>
                        <Select value={office} onValueChange={setOffice}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a building grade">
                                    {office && (
                                        <span>{GRADE_OPTIONS.find(opt => opt.value === office)?.option}</span>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {GRADE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        <div className="font-medium text-sm">{opt.option}</div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Type of Space */}
                    <div >
                        <div className="font-semibold mb-2 text-start text-sm">Select Space Type</div>
                        <Select value={spaceType} onValueChange={setSpaceType}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a space type">
                                    {spaceType && (
                                        <div className="flex items-center gap-2">
                                            <img
                                                src={SPACETYPE_OPTIONS.find(opt => opt.value === spaceType)?.icon || "/placeholder.svg"}
                                                alt={spaceType}
                                                className="h-4 w-4"
                                            />
                                            <span>{SPACETYPE_OPTIONS.find(opt => opt.value === spaceType)?.option}</span>
                                        </div>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {SPACETYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        <div className="flex items-center gap-2">
                                            <img src={opt.icon || "/placeholder.svg"} alt={opt.value} className="h-4 w-4" />
                                            <div>
                                                <div className="font-semibold text-sm">{opt.option}</div>
                                                <div className="text-gray-500 text-xs">{opt.description}</div>
                                            </div>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div >

                </div>

                {/* Area (Covered Area or Number of Desks) */}
                {
                    spaceType !== "desks" ? (
                        <>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <p className="font-semibold text-sm text-start">Covered Area</p>
                                    <p className="font-semibold text-sm text-end">  {units === "sm" ? areaDisplaySqm : areaDisplaySqft} {units === "sm" ? "sqm" : "sqft"}</p>
                                </div>
                                <SliderDemo
                                    value={[localSliderValue]}
                                    onValueChange={handleLocalAreaSliderChange}
                                    min={1}
                                    max={localSliderMax}
                                    step={1}
                                    className="pb-3"
                                    showTooltip
                                    tooltipSuffix={" " + units}
                                />
                                <RadioGroup className="flex gap-8" value={units} onValueChange={setUnits}>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="sm" id="sm" />
                                        <Label htmlFor="sm">
                                            <span className="font-bold mx-1">{localAreaDisplaySqm}</span>
                                            Square Meters
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="sf" id="sf" />
                                        <Label htmlFor="sf">
                                            <span className="font-bold mx-1">{new Intl.NumberFormat().format(localAreaDisplaySqft)}</span>
                                            Square Feet
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <p className="font-semibold text-sm text-start">Number of Desks</p>
                                    <p className="font-semibold text-sm text-end">{spaceType === "desks" ? desks : localAreaDisplaySqm} {spaceType === "desks" ? "Desks" : units === "sm" ? "sqm" : "sqft"}</p>
                                </div>
                                <SliderDemo
                                    value={[desks]}
                                    onValueChange={(value) => setDesks(value[0])}
                                    min={1}
                                    max={100}
                                    step={1}
                                    className="pb-3"
                                    showTooltip
                                    tooltipSuffix=" Desks"
                                />
                            </div>
                        </>
                    )
                }

                {/* Action Buttons */}
                <div className={`p-2 bg-white ${isSticky && isMobile ? 'fixed bottom-0 left-0 right-0 z-[39] border-t border-gray-200' : ''}`}>
                    <div className="flex justify-center md:justify-end">
                        <OhButton onClick={() => setAddListingModal(true)} className="w-full md:w-auto md:px-16">
                            Add Your Listing
                        </OhButton>
                    </div>
                </div>
            </fieldset>
        </>
    )
}